import React, { useState } from 'react';
import { releases } from '../mock/data';
import { Calendar, Gauge, Play, ChevronDown, ChevronUp, X } from 'lucide-react';
import { SiBeatport, SiSpotify, SiApplemusic, SiAmazonmusic, SiYoutube, SiYoutubemusic, SiTidal } from 'react-icons/si';
import { FaSoundcloud } from 'react-icons/fa';

const Releases = () => {
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [expandedPlatforms, setExpandedPlatforms] = useState({});

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <section id="releases" className="relative py-24 bg-gradient-to-b from-black via-zinc-950 to-black">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#0a0a0a_1px,transparent_1px),linear-gradient(to_bottom,#0a0a0a_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-20"></div>
      
      {/* Green accent line */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-500 to-transparent"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4">
            LATEST
            <span className="block text-green-400 mt-2 drop-shadow-[0_0_20px_rgba(0,255,136,0.8)]">
              RELEASES
            </span>
          </h2>
          <div className="w-24 h-1 bg-green-500 mx-auto mt-6 shadow-[0_0_10px_rgba(0,255,136,0.8)]"></div>
        </div>

        {/* Releases Carousel */}
        <div className="relative">
          <div className="overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-green-500/50 scrollbar-track-zinc-800/30">
            <div className="flex gap-6 px-2" style={{ width: 'max-content' }}>
              {releases.map((release) => (
            <div 
              key={release.id}
              className="group relative bg-zinc-900/50 backdrop-blur-sm border border-green-500/20 rounded-xl overflow-hidden hover:border-green-500/60 transition-all duration-500 hover:shadow-[0_0_40px_rgba(0,255,136,0.3)] hover:-translate-y-2 flex-shrink-0"
              style={{ width: '160px', maxHeight: '380px' }}
            >
              {/* Album Artwork */}
              <div className="relative overflow-hidden" style={{ height: '160px' }}>
                <img 
                  src={release.artwork} 
                  alt={release.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60"></div>
                
                {/* Play overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <button 
                    onClick={() => setSelectedRelease(release)}
                    className="p-4 bg-green-500 rounded-full hover:bg-green-400 transition-colors duration-300 shadow-[0_0_30px_rgba(0,255,136,0.6)]"
                  >
                    <Play size={24} className="text-black fill-black" />
                  </button>
                </div>
              </div>

              {/* Release Info */}
              <div className="p-2">
                <h3 className="text-white font-bold text-sm mb-1 group-hover:text-green-400 transition-colors duration-300 line-clamp-1">
                  {release.title}
                </h3>
                <p className="text-gray-400 text-xs mb-1.5 flex items-center gap-1 flex-wrap">
                  {release.artist}
                  {release.comingSoon && (
                    <span className="px-2 py-0.5 bg-green-500/20 border border-green-500/50 text-green-400 text-xs rounded font-semibold animate-pulse">
                      OUT {new Date(release.releaseDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long' }).toUpperCase()}
                    </span>
                  )}
                  {release.isBootleg && (
                    <span className="px-2 py-0.5 bg-yellow-500/20 border border-yellow-500/50 text-yellow-400 text-xs rounded font-semibold">
                      BOOTLEG
                    </span>
                  )}
                  {release.vipExclusive && (
                    <span className="px-2 py-0.5 bg-red-500/20 border border-red-500/50 text-red-400 text-xs rounded font-semibold">
                      VIP BONUS PERK
                    </span>
                  )}
                </p>

                {/* Metadata */}
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                  <div className="flex items-center gap-0.5">
                    <Gauge size={10} className="text-green-400" />
                    <span className="text-xs">{release.bpm}</span>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <Calendar size={10} className="text-green-400" />
                    <span className="text-xs">{new Date(release.releaseDate).getFullYear()}</span>
                  </div>
                </div>

                {/* Streaming Links */}
                <div className="space-y-1">
                  {/* Coming Soon message */}
                  {release.comingSoon && (
                    <div className="p-1.5 bg-green-500/10 border border-green-500/30 rounded text-center">
                      <span className="text-green-400 text-xs font-semibold">
                        Out {new Date(release.releaseDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                    </div>
                  )}
                  
                  {/* Main platforms - always visible */}
                  {!release.comingSoon && (
                  <div className="grid grid-cols-4 gap-1">
                    {release.streamingLinks?.beatport && (
                      <a
                        href={release.streamingLinks?.beatport}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 bg-zinc-800/50 rounded hover:bg-green-500/20 border border-green-500/20 hover:border-green-500 transition-all duration-300 flex items-center justify-center text-gray-400 hover:text-green-400"
                        title="Beatport"
                      >
                        <SiBeatport size={12} />
                      </a>
                    )}
                    {release.streamingLinks?.spotify && (
                      <a
                        href={release.streamingLinks?.spotify}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 bg-zinc-800/50 rounded hover:bg-green-500/20 border border-green-500/20 hover:border-green-500 transition-all duration-300 flex items-center justify-center text-gray-400 hover:text-green-400"
                        title="Spotify"
                      >
                        <SiSpotify size={12} />
                      </a>
                    )}
                    {release.streamingLinks?.appleMusic && (
                      <a
                        href={release.streamingLinks?.appleMusic}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 bg-zinc-800/50 rounded hover:bg-green-500/20 border border-green-500/20 hover:border-green-500 transition-all duration-300 flex items-center justify-center text-gray-400 hover:text-green-400"
                        title="Apple Music"
                      >
                        <SiApplemusic size={12} />
                      </a>
                    )}
                    {release.streamingLinks?.amazon && (
                      <a
                        href={release.streamingLinks?.amazon}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1 bg-zinc-800/50 rounded hover:bg-green-500/20 border border-green-500/20 hover:border-green-500 transition-all duration-300 flex items-center justify-center text-gray-400 hover:text-green-400"
                        title="Amazon Music"
                      >
                        <SiAmazonmusic size={12} />
                      </a>
                    )}
                  </div>
                  )}

                  {release.isBootleg && !release.comingSoon && (
                    <div className="pt-0.5">
                      <div className="p-1 bg-yellow-500/10 border border-yellow-500/30 rounded text-center">
                        <span className="text-yellow-400 text-xs font-semibold">Exclusive</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
              ))}
            </div>
          </div>
          
          {/* Scroll indicator */}
          <div className="text-center mt-4">
            <p className="text-green-400 text-sm animate-pulse">← Scroll left/right to view all releases →</p>
          </div>
        </div>

        {/* Preview Modal */}
        {selectedRelease && (
          <div 
            className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedRelease(null)}
          >
            <div 
              className="bg-gradient-to-b from-zinc-900 to-zinc-950 border-2 border-green-500/40 rounded-2xl p-8 max-w-lg w-full shadow-[0_0_80px_rgba(0,255,136,0.5)] relative animate-fade-in"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close button - positioned much lower for mobile status bar */}
              <button
                onClick={() => setSelectedRelease(null)}
                className="absolute top-20 right-4 sm:top-4 p-3 bg-zinc-800/90 hover:bg-zinc-700 rounded-full transition-all duration-200 hover:scale-110 group shadow-lg z-50 border-2 border-green-500/50"
                aria-label="Close"
              >
                <X size={26} className="text-white group-hover:text-green-400" />
              </button>

              {/* Album Artwork */}
              <div className="mb-6">
                <img 
                  src={selectedRelease.artwork} 
                  alt={selectedRelease.title}
                  className="w-full aspect-square object-cover rounded-xl shadow-2xl border-2 border-green-500/30"
                />
              </div>

              {/* Track Info */}
              <div className="mb-6">
                <h3 className="text-3xl font-bold text-white mb-2 pr-8">{selectedRelease.title}</h3>
                <p className="text-gray-400 mb-3 flex items-center gap-2 flex-wrap text-lg">
                  {selectedRelease.artist}
                  {selectedRelease.isBootleg && (
                    <span className="px-2 py-1 bg-yellow-500/20 border border-yellow-500/50 text-yellow-400 text-xs rounded font-semibold">
                      BOOTLEG
                    </span>
                  )}
                </p>
                <div className="flex gap-4 text-base text-gray-500">
                  <span className="flex items-center gap-1">
                    <Gauge size={16} className="text-green-400" />
                    {selectedRelease.bpm} BPM
                  </span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Calendar size={16} className="text-green-400" />
                    {formatDate(selectedRelease.releaseDate)}
                  </span>
                </div>
              </div>

              {/* TikTok Preview Button */}
              {selectedRelease.tiktokUrl && (
                <a
                  href={selectedRelease.tiktokUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block w-full px-8 py-5 bg-gradient-to-r from-green-500 to-green-600 text-black rounded-xl font-bold text-center text-lg hover:from-green-400 hover:to-green-500 transition-all duration-300 shadow-[0_0_30px_rgba(0,255,136,0.5)] hover:shadow-[0_0_50px_rgba(0,255,136,0.8)] hover:scale-105 flex items-center justify-center gap-3 group"
                >
                  <Play size={24} className="fill-black group-hover:scale-110 transition-transform" />
                  Watch Preview on TikTok
                </a>
              )}

              {/* Streaming Platforms */}
              {Object.keys(selectedRelease.streamingLinks).length > 0 && (
                <div className="mt-6 pt-6 border-t border-green-500/20">
                  <p className="text-sm text-gray-400 mb-3 font-semibold">Stream on:</p>
                  <div className="flex gap-3 flex-wrap">
                    {selectedRelease.streamingLinks?.beatport && (
                      <a
                        href={selectedRelease.streamingLinks?.beatport}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-zinc-800/70 rounded-lg hover:bg-green-500/20 border border-green-500/20 hover:border-green-500 transition-all duration-300 flex items-center gap-2 text-gray-400 hover:text-green-400"
                        title="Beatport"
                      >
                        <SiBeatport size={18} />
                        <span className="text-sm font-medium">Beatport</span>
                      </a>
                    )}
                    {selectedRelease.streamingLinks?.spotify && (
                      <a
                        href={selectedRelease.streamingLinks?.spotify}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-zinc-800/70 rounded-lg hover:bg-green-500/20 border border-green-500/20 hover:border-green-500 transition-all duration-300 flex items-center gap-2 text-gray-400 hover:text-green-400"
                        title="Spotify"
                      >
                        <SiSpotify size={18} />
                        <span className="text-sm font-medium">Spotify</span>
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bottom accent line */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-green-500 to-transparent"></div>
    </section>
  );
};

export default Releases;
